import React from "react";
import styled, { withTheme } from "styled-components";
import { Tab, Tabs, TabList, TabPanel } from "../../components/tabs";
import {
  selectors as routerSelectors,
  actions as routerActions
} from "../../../redux/router";
import { Redirect } from "react-router-dom";
import { selectors as bridgeSelectors } from "../../../redux/bridge";
import { selectors as brandSelectors } from "../../../redux/brand";
import LibraryTab from "./library-tab";
import GroupsTab from "./groups-tab";
import ExploreTab from "./explorer-tab";
import AdminTab from "./admin-tab";
import InteractivesTab from "./interactives-tab";
import { OffScreenStyles } from "../../components/mixins";
import { connect } from "react-redux";
import { t } from "../../core/translate";
import CenteredSpinner from "../../components/spinner/centered-spinner";
import SubscriptionPopover from "./subscription-popover";
import withMatchMedia from "../../components/mediaquery/with-match-media";
import StudentHomeBanner from "./student-home-banner";

const smallTabsMediaQuery = "screen and (max-width: 499px)";

const defaultTabs = (theme, scrolled) => ({
  listStyle: "none",
  margin: "0",
  width: "100%",
  display: "flex",
  height: "44px",
  justifyContent: "left",
  padding: "0 30px",
  position: "fixed",
  marginTop: "0",
  boxShadow: scrolled
    ? `0px 0px 5px ${theme.color.base.getShade(10)}`
    : `0px 0px 5px transparent`
});

const StyledTabList = styled(TabList)`
  background: ${props => props.theme.color.base.getShade(9)};
  z-index: 2;
  margin-top: -44px;
  transition: margin-top 250ms linear;
`;

const ScrollOffset = styled.div`
  transition: padding-top 250ms linear;
  padding-top: ${props => (props.tabsVisible ? "44px" : 0)};
  padding-bottom: 60px;
`;

const StyledTab = styled(Tab)`
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 100%;
  min-width: 114px;
  margin: 0 1.5px;
  font-size: 1.5rem;
  font-weight: 500;
  color: ${props => props.theme.vstui.color.white};
  @media ${smallTabsMediaQuery} {
    min-width: 80px;
  }
  &.react-tabs__tab--selected {
    background: ${props => props.theme.color.base.getShade(10)};
    hr {
      width: 100%;
      height: 0.4rem;
      background: ${props => props.theme.color.base.getShade(4)};
    }
  }
  hr {
    height: 0px;
    border: none;
    transition: 0.2s ease-in-out;
    margin: 0 auto;
    right: 0;
    left: 0;
    position: absolute;
    bottom: 0;
    width: 0;
  }
  &:hover {
    hr {
      width: 100%;
      height: 0.4rem;
      background: ${props => props.theme.color.base.getShade(4)};
    }
  }
`;

const PrimaryHeadingTag = styled.h1`
  ${OffScreenStyles};
`;

const StyledSubscriptionPopover = styled(SubscriptionPopover)`
  position: fixed;
  z-index: 100;
  top: 114px;
  margin-right: 20px;
`;

class LibraryTabBar extends React.Component {
  constructor() {
    super();
    this.state = { scrolled: false, exploreTabX: -10000, exploreTabWidth: 100 };
    this.onWindowScroll = this.onWindowScroll.bind(this);
  }

  componentDidMount() {
    window.addEventListener("scroll", this.onWindowScroll);
  }

  componentDidUpdate() {
    if (this.containerRef) {
      const tab = this.containerRef.querySelector("[data-tab-id='explore']");
      if (tab && tab.offsetLeft !== this.state.exploreTabX) {
        this.setState({
          exploreTabX: tab.offsetLeft,
          exploreTabWidth: tab.offsetWidth
        });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.onWindowScroll);
  }

  onWindowScroll(e) {
    if (window.scrollY > 0 && !this.state.scrolled) {
      this.setState({ scrolled: true });
    } else if (window.scrollY === 0 && this.state.scrolled) {
      this.setState({ scrolled: false });
    }
  }

  getTabs() {
    const {
      bridgeUserFetching,
      bridgeUser,
      hasSubscriptions,
      location,
      hasInteractives,
      interactivesEnabled,
      adminEnabled,
      libraryEnabled,
      subscriptionsEnabled,
      native
    } = this.props;
    const tabs = [];
    tabs.push({
      path: "",
      title: t("main-menu.library", "Library"),
      enabled: libraryEnabled || true, // we can't actually hide the library right now
      component: <LibraryTab native={native} />
    });
    tabs.push({
      path: "classes",
      title: t("base.groups", "Groups"),
      enabled:
        (bridgeUser.hasGroups ||
          (bridgeUserFetching && location.hash.indexOf("classes") >= 0)) &&
        adminEnabled,
      component: <GroupsTab />
    });
    tabs.push({
      path: "explore",
      title: t("base.explore", "Explore"),
      enabled:
        (hasSubscriptions ||
          (bridgeUserFetching && location.hash.indexOf("explore") >= 0)) &&
        subscriptionsEnabled,
      component: <ExploreTab />
    });
    tabs.push({
      path: "interactives",
      title: t("base.interactives", "Interactives"),
      enabled:
        (hasInteractives ||
          (bridgeUserFetching && location.hash.indexOf("interactives") >= 0)) &&
        interactivesEnabled,
      component: <InteractivesTab />
    });
    if (!native) {
      tabs.push({
        path: "admin",
        title: t("base.admin", "Admin"),
        enabled:
          (bridgeUser.hasAdmin ||
            (bridgeUserFetching && location.hash.indexOf("admin") >= 0)) &&
          adminEnabled,
        component: <AdminTab />
      });
    }

    return tabs;
  }

  renderMainLibraryContent(visibleTabs, selectedIndex, tabsVisible) {
    const { push, bridgeUserFetching, isSmallTabs } = this.props;
    const tabStyles =
      tabsVisible && !bridgeUserFetching
        ? defaultTabs(this.props.theme, this.state.scrolled)
        : OffScreenStyles;

    const SingleTab = !!visibleTabs[selectedIndex] ? (
      visibleTabs[selectedIndex].component
    ) : (
      <CenteredSpinner />
    );
    const TabsContainer = (
      <Tabs
        selectedIndex={Math.max(0, selectedIndex)}
        onSelect={index => {
          push(visibleTabs[index].path);
          window.scrollTo(0, 0);
        }}
      >
        <StyledTabList style={tabStyles}>
          {visibleTabs.map(tab => (
            <StyledTab key={`tab-${tab.path}`} data-tab-id={tab.path}>
              {tab.title}
              <hr />
            </StyledTab>
          ))}
        </StyledTabList>
        {!isSmallTabs && (
          <StyledSubscriptionPopover
            arrowOffset={this.state.exploreTabWidth / 2}
            targetOffset={this.state.exploreTabX}
            boxOffset={8}
          />
        )}
        <ScrollOffset tabsVisible={tabsVisible}>
          {visibleTabs.map(tab => (
            <TabPanel key={`panel-${tab.path}`}>{tab.component}</TabPanel>
          ))}
        </ScrollOffset>
      </Tabs>
    );
    return tabsVisible ? TabsContainer : SingleTab;
  }

  render() {
    const { location, dispatch, native, enableStudentBanner } = this.props;
    const tabs = this.getTabs();
    const visibleTabs = tabs.filter(tab => tab.enabled);
    const selectedIndex = visibleTabs.findIndex(tab =>
      tab.path === "" && location.hash === "#/"
        ? true
        : location.hash.indexOf(tab.path) > 0
    );
    const tabsVisible = visibleTabs.length > 1 && !native;
    const showStudentBanner = enableStudentBanner && !native;

    if (
      selectedIndex < 0 &&
      tabs.some(tab => location.hash.indexOf(tab.path) > 0)
    ) {
      dispatch({ type: "REDIRECT IN LIBRARY TAB BAR - " + location.search });
      return <Redirect to={`/${location.search}`} />;
    } else if (selectedIndex < 0) {
      return null;
    } else {
      return (
        <div ref={el => (this.containerRef = el)}>
          <PrimaryHeadingTag>
            {visibleTabs[selectedIndex].title}
          </PrimaryHeadingTag>
          {showStudentBanner && <StudentHomeBanner tabsVisible={tabsVisible} />}
          {this.renderMainLibraryContent(
            visibleTabs,
            selectedIndex,
            tabsVisible
          )}
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  location: routerSelectors.getCurrentRoute(state),
  native: routerSelectors.isNative(state),
  bridgeUserFetching: bridgeSelectors.isUserFetching(state),
  bridgeUser: bridgeSelectors.getUser(state),
  hasSubscriptions: bridgeSelectors.hasSubscriptions(state),
  hasInteractives: bridgeSelectors.hasInteractives(state),
  libraryEnabled: brandSelectors.getBrandValue(state, "Library.enabled", true),
  adminEnabled: brandSelectors.getBrandValue(state, "Rostering.enabled", false),

  subscriptionsEnabled: brandSelectors.getBrandValue(
    state,
    "Subscriptions.enabled",
    true
  ),
  interactivesEnabled: brandSelectors.getBrandValue(
    state,
    "Interactives.enabled",
    true
  ),
  enableStudentBanner: brandSelectors.getBrandValue(
    state,
    "StudentHome.enabled"
  )
});

const mapDispatchToProps = dispatch => ({
  push: path => dispatch(routerActions.push(path)),
  dispatch
});

export default withMatchMedia(
  smallTabsMediaQuery,
  "isSmallTabs"
)(withTheme(connect(mapStateToProps, mapDispatchToProps)(LibraryTabBar)));
